// src/services/grnService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class GRNService {
  /**
   * Get all GRNs
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllGRNs(params = {}) {
    try {
      console.log('📦 Fetching all GRNs with params:', params)
      
      const response = await ApiService.get(AppURLs.GRN.GET_ALL, params)
      
      console.log('✅ GRNs fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching GRNs:', error)
      throw error
    }
  }

  /**
   * Get GRN by ID
   * @param {string} id - GRN ID
   * @returns {Promise<Object>} - API response
   */
  async getGRNById(id) {
    try {
      console.log('📦 Fetching GRN by ID:', id)
      
      if (!id) {
        throw new Error('GRN ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.GRN.GET_BY_ID, { id })
      console.log('📦 Using endpoint:', endpoint)
      
      const response = await ApiService.get(endpoint)
      
      console.log('✅ GRN fetched successfully:', response)
      console.log('📦 GRN data structure:', JSON.stringify(response.data, null, 2))
      
      return response
    } catch (error) {
      console.error('❌ Error fetching GRN:', error)
      throw error
    }
  }

  /**
   * Create new GRN - ENHANCED WITH BETTER ERROR HANDLING
   * @param {Object} grnData - GRN data formatted for API
   * @returns {Promise<Object>} - API response
   */
  async createGRN(grnData) {
    try {
      console.log('📦 Creating GRN with data:', grnData)
      
      // Validate the data before sending
      if (!grnData.purchaseOrder) {
        throw new Error('Purchase order is required')
      }
      
      if (!grnData.receivedBy) {
        throw new Error('Received by is required')
      }
      
      if (!grnData.receivedDate) {
        throw new Error('Received date is required')
      }
      
      if (!grnData.receivedItems || !Array.isArray(grnData.receivedItems) || grnData.receivedItems.length === 0) {
        throw new Error('At least one received item is required')
      }
      
      // Validate each received item
      grnData.receivedItems.forEach((item, index) => {
        if (!item.poItemId) {
          throw new Error(`Item ${index + 1}: Purchase order item ID is required`)
        }
        if (typeof item.receivedQuantity !== 'number' || item.receivedQuantity <= 0) {
          throw new Error(`Item ${index + 1}: Valid received quantity is required`)
        }
        if (typeof item.unitPrice !== 'number' || item.unitPrice < 0) {
          throw new Error(`Item ${index + 1}: Valid unit price is required`)
        }
      })
      
      // Use AppURLs if available, fallback to direct endpoint
      const endpoint = AppURLs?.GRN?.CREATE || '/goods-receipt-notes'
      console.log('📦 Using GRN create endpoint:', endpoint)
      
      const response = await ApiService.post(endpoint, grnData)
      
      console.log('✅ GRN created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating GRN:', error)
      
      // Enhanced error handling - Always prioritize backend error message
      if (error.response) {
        // Server responded with error status
        const status = error.response.status
        const backendMessage = error.response.data?.message || error.message
        
        // Always use the backend error message if available
        if (backendMessage && backendMessage !== 'Request failed with status code ' + status) {
          throw new Error(backendMessage)
        }
        
        // Fallback to generic messages only if no backend message
        if (status === 400) {
          throw new Error('Validation Error: Please check your input.')
        } else if (status === 404) {
          throw new Error('Resource not found. Please check your configuration.')
        } else if (status === 500) {
          throw new Error('Server error. Please try again later.')
        } else {
          throw new Error(`HTTP ${status}: An error occurred`)
        }
      } else if (error.code === 'ECONNABORTED') {
        // Timeout error
        throw new Error('Request timed out. The server may be busy.')
      } else if (error.message.includes('Network Error')) {
        throw new Error('Network error. Please check your connection.')
      } else {
        // Re-throw original error
        throw error
      }
    }
  }

  /**
   * Update GRN
   * @param {string} id - GRN ID
   * @param {Object} grnData - Updated GRN data formatted for API
   * @returns {Promise<Object>} - API response
   */
  async updateGRN(id, grnData) {
    try {
      console.log('📦 Updating GRN:', id, 'with data:', grnData)
      
      if (!id) {
        throw new Error('GRN ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.GRN.UPDATE, { id })
      const response = await ApiService.patch(endpoint, grnData)
      
      console.log('✅ GRN updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating GRN:', error)
      throw error
    }
  }

  /**
   * Delete GRN
   * @param {string} id - GRN ID
   * @returns {Promise<Object>} - API response
   */
  async deleteGRN(id) {
    try {
      console.log('📦 Deleting GRN:', id)
      
      if (!id) {
        throw new Error('GRN ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.GRN.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ GRN deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting GRN:', error)
      throw error
    }
  }

  /**
   * Get available purchase orders for GRN creation - ENHANCED
   * @param {Object} params - Query parameters (e.g., status: 'pending' or 'approved')
   * @returns {Promise<Object>} - API response
   */
  async getAvailablePurchaseOrders(params = {}) {
    try {
      console.log('📦 Fetching available purchase orders for GRN...', params)
      
      // Use AppURLs if available, fallback to direct endpoint
      const endpoint = AppURLs?.GRN?.GET_PURCHASE_ORDERS || '/purchase-orders'
      console.log('📦 Using PO endpoint:', endpoint)
      
      const response = await ApiService.get(endpoint, params)
      
      console.log('✅ Available purchase orders fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching available purchase orders:', error)
      
      // Try fallback endpoint if the primary fails
      try {
        console.log('📦 Trying fallback endpoint: /purchase-orders')
        const fallbackResponse = await ApiService.get('/purchase-orders', params)
        console.log('✅ Purchase orders fetched via fallback:', fallbackResponse)
        return fallbackResponse
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError)
        throw error
      }
    }
  }

  /**
   * Get all GRN payments
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllGRNPayments(params = {}) {
    try {
      console.log('💰 Fetching all GRN payments with params:', params)
      
      const endpoint = AppURLs?.GRN_PAYMENTS?.GET_ALL || '/grn-payments'
      const response = await ApiService.get(endpoint, params)
      
      console.log('✅ GRN payments fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching GRN payments:', error)
      throw error
    }
  }

  /**
   * Create GRN payment
   * @param {Object} paymentData - Payment data formatted for API
   * @returns {Promise<Object>} - API response
   */
  async createGRNPayment(paymentData) {
    try {
      console.log('💰 Creating GRN payment with data:', paymentData)
      
      const endpoint = AppURLs?.GRN_PAYMENTS?.CREATE || '/grn-payments'
      const response = await ApiService.post(endpoint, paymentData)
      
      console.log('✅ GRN payment created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating GRN payment:', error)
      throw error
    }
  }

  /**
   * Get payments for specific GRN
   * @param {string} grnId - GRN ID
   * @returns {Promise<Object>} - API response
   */
  async getGRNPayments(grnId) {
    try {
      console.log('💰 Fetching payments for GRN:', grnId)
      
      if (!grnId) {
        throw new Error('GRN ID is required')
      }

      const endpoint = AppURLs?.GRN_PAYMENTS?.GET_BY_GRN 
        ? ApiService.replaceUrlParams(AppURLs.GRN_PAYMENTS.GET_BY_GRN, { grnId })
        : `/grn-payments/grn/${grnId}`
        
      const response = await ApiService.get(endpoint)
      
      console.log('✅ GRN payments fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching GRN payments:', error)
      throw error
    }
  }

  /**
   * Delete GRN payment
   * @param {string} paymentId - Payment ID
   * @returns {Promise<Object>} - API response
   */
  async deleteGRNPayment(paymentId) {
    try {
      console.log('💰 Deleting GRN payment:', paymentId)
      
      if (!paymentId) {
        throw new Error('Payment ID is required')
      }

      const endpoint = AppURLs?.GRN_PAYMENTS?.DELETE 
        ? ApiService.replaceUrlParams(AppURLs.GRN_PAYMENTS.DELETE, { id: paymentId })
        : `/grn-payments/${paymentId}`
        
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ GRN payment deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting GRN payment:', error)
      throw error
    }
  }

  /**
   * Format payment data for API
   * @param {Object} paymentData - Raw payment form data
   * @returns {Object} - Formatted payment data for API
   */
  formatPaymentForAPI(paymentData) {
    return {
      grn: paymentData.grnId,
      paymentType: paymentData.paymentMethod || paymentData.paymentType,
      amountPaid: parseFloat(paymentData.amountPaid) || 0,
      paymentDate: paymentData.paymentDate + (paymentData.paymentDate.includes('T') ? '' : 'T00:00:00.000Z'),
      note: paymentData.notes || paymentData.note || ''
    }
  }

  /**
   * Get GRN statistics and summary data
   * @returns {Promise<Object>} - Statistics data
   */
  async getGRNStatistics() {
    try {
      console.log('📊 Fetching GRN statistics...')
      
      // Get all GRNs and payments to calculate statistics
      const [grnsResponse, paymentsResponse] = await Promise.all([
        this.getAllGRNs(),
        this.getAllGRNPayments()
      ])
      
      const grns = grnsResponse?.data?.goodsReceiptNotes || grnsResponse?.data || []
      const payments = paymentsResponse?.data?.grnPayments || paymentsResponse?.data || []
      
      // Calculate statistics
      const totalGRNs = grns.length
      const totalAmount = grns.reduce((sum, grn) => sum + (grn.totalAmount || 0), 0)
      const paidAmount = payments.reduce((sum, payment) => sum + (payment.amountPaid || 0), 0)
      const remainingAmount = grns.reduce((sum, grn) => sum + (grn.remainingPayment || 0), 0)
      
      const statistics = {
        totalGRNs,
        totalAmount,
        paidAmount,
        remainingAmount,
        averageGRNValue: totalGRNs > 0 ? totalAmount / totalGRNs : 0,
        paymentCompletionRate: totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0
      }
      
      console.log('✅ GRN statistics calculated:', statistics)
      return { success: true, data: statistics }
    } catch (error) {
      console.error('❌ Error fetching GRN statistics:', error)
      throw error
    }
  }
}

export default new GRNService()